import PropTypes from 'prop-types';
import React from 'react';
import { logErrorToDatadog } from '../utils/logging';

/**
 * Reports errors thrown while rendering the chatbot, and renders nothing.
 *
 * This exists purely so render crashes stay visible in Datadog: the filter in
 * `utils/logging.js` deliberately drops every uncaught page error, since there
 * is no reliable way to tell ours from the rest of the page's once it reaches
 * `window.onerror`.
 *
 * It deliberately renders no fallback UI. Without a boundary, an uncaught
 * render error makes React unmount the tree and leaves the chatbot area blank;
 * rendering nothing here reproduces that exactly, so this changes what is
 * logged and nothing about what the Veteran sees.
 *
 * React does not route event-handler or async errors to a boundary, so those
 * still report from their own call sites.
 */
export default class ChatbotErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logErrorToDatadog(
      true,
      `Chatbot render error: ${error?.message || 'unknown error'}`,
      {
        componentStack: errorInfo?.componentStack,
        errorName: error?.name,
        stack: error?.stack,
      },
    );
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

ChatbotErrorBoundary.propTypes = {
  children: PropTypes.node,
};
